import { useEffect, useState } from 'react';
import ChatPanel, { type ChatConnection } from '@/components/ChatPanel';

interface OpenThreadResponse {
    acsThreadId: string;
    chatThreadId: number;
    acsUserId: string;
    token: string;
    expiresOn: string;
    endpoint: string;
    online: number;
}

interface Props {
    apiFetch: <T>(path: string, options?: RequestInit) => Promise<T>;
    facilityCode: string;
    facilityName: string;
    carrierDisplayName: string;
    scope: { type: 'APPOINTMENT'; appointmentId: number; label: string }
          | { type: 'GENERAL'; label: string };
    onClose: () => void;
}

export default function CarrierChatModal({ apiFetch, facilityCode, facilityName, carrierDisplayName, scope, onClose }: Props) {
    const [connection, setConnection] = useState<ChatConnection | null>(null);
    const [online, setOnline] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const body = {
                    facilityCode,
                    scopeType: scope.type,
                    appointmentId: scope.type === 'APPOINTMENT' ? scope.appointmentId : null,
                };
                const res = await apiFetch<OpenThreadResponse>('chat/threads/open', {
                    method: 'POST',
                    body: JSON.stringify(body),
                });
                if (cancelled) return;
                setOnline(res.online);
                setConnection({
                    endpoint: res.endpoint,
                    token: res.token,
                    acsThreadId: res.acsThreadId,
                    acsUserId: res.acsUserId,
                    displayName: carrierDisplayName,
                });
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Could not open chat.');
            }
        })();
        return () => { cancelled = true; };
    }, [apiFetch, facilityCode, scope, carrierDisplayName]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col" style={{ height: '70vh', maxHeight: '600px' }}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">{scope.label}</h2>
                        <div className="flex items-center gap-1.5 text-xs mt-0.5">
                            <span className="text-gray-500">{facilityName}</span>
                            {online !== null && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span className={`inline-flex items-center gap-1 ${online > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${online > 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                                        {online > 0
                                            ? `${online} scheduler${online === 1 ? '' : 's'} online`
                                            : 'No schedulers online — they\u2019ll see this next login'}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>
                <div className="flex-1 min-h-0 px-4 py-2">
                    {error ? (
                        <div className="text-sm text-red-600 p-4">{error}</div>
                    ) : connection ? (
                        <ChatPanel connection={connection} />
                    ) : (
                        <div className="text-sm text-gray-400 p-4 animate-pulse">Opening chat…</div>
                    )}
                </div>
            </div>
        </div>
    );
}
